import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, Search, UserCheck, ShieldAlert, ArrowLeft } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, useApi } from "@/lib/api";
import { fmtDateTime, ROLE_LABEL } from "@/lib/format";
import { StatusBadge, StatusSteps, RelatedTx, Timeline, ReplyBox, toForm } from "@/components/help/TicketParts";

const FILTERS = [["aktif", "Aktif"], ["baru", "Baru"], ["mine", "Milik Saya"], ["menunggu_anggota", "Menunggu Anggota"], ["selesai", "Selesai"], ["", "Semua"]];
const NEXT = [["diproses", "Sedang diproses"], ["menunggu_anggota", "Menunggu anggota"], ["selesai", "Selesai"]];

const Actions = ({ t, user, staff, run }) => {
  const [note, setNote] = useState("");
  const isSuper = user.role === "superadmin";
  const other = t.assigned_to && t.assigned_to !== user.user_id;
  const locked = other && !isSuper;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3" data-testid="help-admin-actions">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="mr-auto">Ditangani: <b data-testid="help-admin-assignee">{t.assigned_name || "Belum ada"}</b></span>
        {!t.assigned_to && <button onClick={() => run("claim")} data-testid="help-claim" className="flex items-center gap-1 bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg"><UserCheck className="h-3.5 w-3.5" />Ambil</button>}
        {other && isSuper && <button onClick={() => run("claim")} data-testid="help-takeover" className="flex items-center gap-1 bg-rose-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg"><ShieldAlert className="h-3.5 w-3.5" />Ambil Alih</button>}
        {!locked && (
          <Select value="" onValueChange={(v) => run("assign", { to_user_id: v })}>
            <SelectTrigger className="h-8 w-[160px] text-xs" data-testid="help-assign-select"><SelectValue placeholder="Alihkan ke..." /></SelectTrigger>
            <SelectContent>{staff?.filter((s) => s.user_id !== t.assigned_to).map((s) => <SelectItem key={s.user_id} value={s.user_id}>{s.name} • {s.position || ROLE_LABEL[s.role]}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>
      {locked ? <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2.5" data-testid="help-locked">Tiket ini sedang ditangani {t.assigned_name}. Minta dialihkan untuk mengubahnya.</p> : (
        <>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan untuk anggota (opsional)" data-testid="help-status-note"
            className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm" />
          <div className="flex flex-wrap gap-2">
            {NEXT.map(([k, l]) => (
              <button key={k} onClick={() => run("status", { status: k, note }).then(() => setNote(""))} disabled={t.status === k && !note} data-testid={`help-status-${k}`}
                className={`text-xs font-semibold px-3 py-2 rounded-lg border disabled:opacity-40 ${k === "selesai" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-slate-200"}`}>Tandai {l}</button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default function AdminHelp() {
  const { user } = useAuth();
  const [filter, setFilter] = useState("aktif");
  const [q, setQ] = useState("");
  const [list, setList] = useState(null);
  const [active, setActive] = useState(null);
  const [t, setT] = useState(null);
  const { data: meta } = useApi("/help/meta");
  const { data: staff } = useApi("/chat-admin/staff");

  const loadList = useCallback(() => api.get("/help-admin/tickets", { params: { status: filter || undefined, q: q || undefined } }).then((r) => setList(r.data)).catch(() => {}), [filter, q]);
  const loadOne = useCallback(() => active && api.get(`/help/tickets/${active}`).then((r) => setT(r.data)).catch(() => {}), [active]);
  useEffect(() => { loadList(); const i = setInterval(loadList, 10000); return () => clearInterval(i); }, [loadList]);
  useEffect(() => { setT(null); loadOne(); }, [loadOne]);

  const run = async (kind, body) => {
    try {
      const { data } = await api.post(`/help-admin/tickets/${active}/${kind}`, body);
      setT(data);
      loadList();
      window.dispatchEvent(new Event("cu-help-changed"));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const reply = async (text, files) => {
    try {
      const { data } = await api.post(`/help-admin/tickets/${active}/reply`, toForm({ text }, files));
      setT(data);
      loadList();
    } catch (e) {
      toast.error(errMsg(e));
      throw e;
    }
  };

  return (
    <div data-testid="admin-help-page">
      <div className="flex items-center gap-3 mb-4">
        <span className="h-11 w-11 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-700 text-white flex items-center justify-center"><LifeBuoy className="h-5 w-5" /></span>
        <div><h1 className="text-2xl font-bold text-slate-900">Pusat Bantuan</h1><p className="text-sm text-slate-500">Tiket kendala anggota: tangani, beri status, dan selesaikan.</p></div>
      </div>
      <div className="grid lg:grid-cols-[380px_1fr] gap-4">
        <div className={`bg-white border border-slate-200 rounded-2xl overflow-hidden ${active ? "hidden lg:block" : ""}`}>
          <div className="p-3 border-b border-slate-100 space-y-2">
            <div className="flex items-center gap-2 bg-slate-100 rounded-xl px-3 h-10"><Search className="h-4 w-4 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="No. tiket, nama, judul" className="bg-transparent outline-none text-sm flex-1" data-testid="help-admin-search" /></div>
            <div className="flex flex-wrap gap-1.5">{FILTERS.map(([k, l]) => (
              <button key={k || "all"} onClick={() => setFilter(k)} data-testid={`help-filter-${k || "all"}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold ${filter === k ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
            ))}</div>
          </div>
          <div className="max-h-[70vh] overflow-y-auto" data-testid="help-admin-list">
            {list?.length === 0 && <p className="text-sm text-center text-slate-500 py-10">Tidak ada tiket</p>}
            {list?.map((i) => (
              <button key={i.ticket_id} onClick={() => setActive(i.ticket_id)} data-testid={`help-admin-ticket-${i.ticket_id}`}
                className={`w-full text-left px-3 py-3 border-b border-slate-50 hover:bg-slate-50 ${active === i.ticket_id ? "bg-emerald-50" : ""}`}>
                <div className="flex items-center gap-2"><span className="font-mono text-[11px] font-bold text-slate-500">{i.ticket_no}</span><StatusBadge status={i.status} label={i.status_label} /></div>
                <div className="font-semibold text-sm truncate mt-0.5">{i.subject}</div>
                <div className="text-[11px] text-slate-500">{i.member_name} • {i.category_label} • {i.assigned_name || "Belum diambil"} • {fmtDateTime(i.updated_at)}</div>
              </button>
            ))}
          </div>
        </div>
        <div className={`space-y-4 ${active ? "" : "hidden lg:block"}`}>
          {!active && <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-sm text-slate-500">Pilih tiket di sebelah kiri</div>}
          {active && t && meta && (
            <>
              <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
                <button className="lg:hidden flex items-center gap-1 text-sm text-slate-600" onClick={() => setActive(null)} data-testid="help-admin-back"><ArrowLeft className="h-4 w-4" />Kembali</button>
                <div className="flex items-start justify-between gap-2">
                  <div><div className="font-mono text-xs font-bold text-slate-500">{t.ticket_no} • {t.category_label}</div>
                    <div className="font-bold text-lg text-slate-900" data-testid="help-admin-subject">{t.subject}</div>
                    <div className="text-xs text-slate-500">{t.member_name} ({t.member_no}) • dibuat {fmtDateTime(t.created_at)}</div></div>
                  <StatusBadge status={t.status} label={t.status_label} testid="help-admin-status" />
                </div>
                <StatusSteps status={t.status} />
                <RelatedTx r={t.related} />
              </div>
              <Actions t={t} user={user} staff={staff} run={run} />
              <Timeline t={t} statuses={meta.statuses} />
              {!(t.assigned_to && t.assigned_to !== user.user_id && user.role !== "superadmin") && <ReplyBox onSend={reply} placeholder="Balas anggota" testid="help-admin-reply" />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
